import { Component, inject, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { PlatformService } from '../../core/service/platform.service';
import { getBrand } from '../../core/utils/brand';
import { getTokenFromUrl } from '../../core/utils/token';
import { UnlockPage } from '../../features/unlock/unlock.page';

@Component({
  selector: 'app-blank',
  standalone: true,
  imports: [UnlockPage],
  templateUrl: './blank.page.html',
  styleUrl: './blank.page.css',
})
export class BlankPage {
  protected readonly platformService = inject(PlatformService);
  protected readonly brand = getBrand();
  protected pendingRedirectUrl: string | null = null;
  protected readonly safariUrl =
    typeof window !== 'undefined' && window.location?.href
      ? this.platformService.toSafariUrl(window.location.href, this.brand)
      : '';

  protected readonly showAndroidDialog = signal(true);

  private readonly token = getTokenFromUrl();

  openApp(): void {
    this.platformService.openApp(this.token, this.brand, this.pendingRedirectUrl);
  }

  dismissDialog(): void {
    this.showAndroidDialog.set(false);
  }

  constructor() {
    // Prevent UnlockPage from firing its own Android intent — blank.page owns this flow
    if (this.platformService.isAndroid && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('androidIntentFired', '1');
    }
    const currentDomain =
      typeof window !== 'undefined' && window.location?.href ? window.location.href : '';
    if (this.platformService.isIos && currentDomain) {
      if (this.hasSubdomain(currentDomain)) {
        const url = this.transformUrl(currentDomain);
        if (environment.iosAppClipLandingPage) {
          this.pendingRedirectUrl = url;
        } else {
          window.location.replace(url);
        }
      } else {
        try {
          const parsed = new URL(currentDomain);
          if (!parsed.searchParams.has('id')) {
            const url = this.buildRootRedirect(currentDomain);
            if (url) {
              if (environment.iosAppClipLandingPage) {
                this.pendingRedirectUrl = url;
              } else {
                window.location.replace(url);
              }
            }
          }
        } catch (e) {
          console.warn('[BlankPage] failed to parse URL for redirect:', currentDomain, e);
        }
      }
    }
  }

  buildRootRedirect(inputUrl: string): string | null {
    if (!inputUrl || typeof inputUrl !== 'string') return null;
    try {
      const url = new URL(inputUrl);
      const pathSegments = url.pathname.split('/').filter(Boolean);
      const token = pathSegments.pop() ?? '';
      if (!token) return null;
      return `${url.protocol}//${url.host}/?id=${token}`;
    } catch {
      return null;
    }
  }

  transformUrl(inputUrl: string): string {
    if (!inputUrl || typeof inputUrl !== 'string') return '';
    try {
      const url = new URL(inputUrl);
      const hostnameParts = url.hostname.split('.');
      const subdomain = hostnameParts[0];
      const pathSegments = url.pathname.split('/').filter(Boolean);
      const token = pathSegments.pop() ?? '';
      const cleanHost = url.host.replace(`${subdomain}.`, '');
      return `${url.protocol}//${cleanHost}/${subdomain}?id=${token}`;
    } catch {
      return inputUrl;
    }
  }

  hasSubdomain(urlInput: string): boolean {
    if (!urlInput || typeof urlInput !== 'string') return false;
    try {
      const url = new URL(urlInput);
      const hostname = url.hostname;

      if (!hostname || /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(hostname)) {
        return false;
      }

      const canonicalHosts = [environment.hostName, ...environment.altHostNames];
      if (canonicalHosts.includes(hostname)) {
        return false;
      }

      // Brand domains (e.g. works.key.hooddisrupt.com) are first-class hosts with
      // their own registered App Clip — not tenant subdomains to relocate.
      if (
        environment.worksHosts.some(
          (host: string) => hostname === host || hostname.endsWith(`.${host}`),
        )
      ) {
        return false;
      }

      const parts = hostname.split('.');

      if (canonicalHosts.some((host: string) => hostname.includes(host))) {
        return parts.length > 1;
      }

      return parts.length > 2;
    } catch {
      return false;
    }
  }
}
